import { Controller } from "@hotwired/stimulus"

// Generic searchable select popover.
// Click a readonly input to open a popover with search + scrollable options list.
// Selecting an option sets the hidden field value and shows the label in the display.

export default class extends Controller {
  static targets = ["display", "hidden", "popover", "search", "list",
    "browser", "browserSearch", "browserList", "browserCount", "aiBtn", "aiStatus", "chips"]
  static values = {
    options: Array,      // [{id, name, group?}]
    placeholder: { type: String, default: "Select..." },
    browseTitle: { type: String, default: "Select..." },
    multi: { type: Boolean, default: false },  // pick several (comma ids in the hidden field)
    suggestUrl: String                          // AI activity suggestions endpoint
  }

  connect() {
    this._onClickOutside = (e) => {
      if (this.hasPopoverTarget && !this.element.contains(e.target)) {
        this._close()
      }
    }
    document.addEventListener("click", this._onClickOutside)
    // Multi mode: show the saved selections as chips right away.
    if (this.multiValue) this._renderChips()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  // Restore a saved draft's ids (multi: several; single: the first).
  setSelection(ids) {
    const list = (ids || []).map(String).filter(Boolean)
    if (this.multiValue) return this._commitMulti(list)
    const opt = (this.optionsValue || []).find(o => String(o.id) === list[0])
    if (opt) this._commit(String(opt.id), opt.name)
  }

  toggle() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.contains("hidden") ? this._open() : this._close()
  }

  _open() {
    this._renderList("")
    this.popoverTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
  }

  _close() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this._renderList(q)
  }

  _renderList(query) {
    if (!this.hasListTarget) return

    const options = this.optionsValue || []
    const currentId = this.hasHiddenTarget ? this.hiddenTarget.value : ""

    const filtered = query
      ? options.filter(o => o.name.toLowerCase().includes(query))
      : options

    let html = ""
    if (filtered.length === 0) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">${query ? "No matches" : "No options"}</div>`
    } else {
      html = filtered.map(o => {
        const selected = String(o.id) === String(currentId)
        return `<div data-action="click->searchable-select#select"
                     data-id="${o.id}"
                     data-name="${this._esc(o.name)}"
                     class="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-50 ${selected ? 'bg-brand/5 text-brand font-medium' : 'text-gray-700'}">
          ${this._esc(o.name)}
        </div>`
      }).join("")
    }

    this.listTarget.innerHTML = html
  }

  select(event) {
    const { id, name } = event.currentTarget.dataset
    this._commit(id, name)
    this._close()
  }

  _commit(id, name) {
    if (this.hasHiddenTarget) this.hiddenTarget.value = id
    if (this.hasDisplayTarget) {
      this.displayTarget.value = name
      this.displayTarget.classList.remove("text-text-muted")
      this.displayTarget.classList.add("text-gray-900")
    }
  }

  // ── Browse pop-up (Kloepfer item 11) ───────────────────────────
  // A roomy, phase-grouped, searchable view of the whole schedule — much
  // easier than scanning the small dropdown by name. Single-select: picking
  // commits and closes, matching the inline behavior.

  openBrowser() {
    if (!this.hasBrowserTarget) return
    this._close()
    this.browserTarget.classList.remove("hidden")
    if (this.hasBrowserSearchTarget) {
      this.browserSearchTarget.value = ""
      this.browserSearchTarget.focus()
    }
    this._renderBrowser("")
    this._updateBrowserCount()
  }

  // ── Multi-select mode (Phelipe 2026-07-23): an action item can link
  // several activities. The hidden field carries comma-separated ids; the
  // display shows "First name +N more".

  _multiIds() {
    return (this.hasHiddenTarget ? this.hiddenTarget.value : "")
      .split(",").map(s => s.trim()).filter(Boolean)
  }

  _commitMulti(ids) {
    if (this.hasHiddenTarget) this.hiddenTarget.value = ids.join(",")
    if (this.hasDisplayTarget) {
      const names = ids.map(id => (this.optionsValue || []).find(o => String(o.id) === id)?.name).filter(Boolean)
      this.displayTarget.value = names.length <= 1 ? (names[0] || "") : `${names[0]} +${names.length - 1} more`
      this.displayTarget.classList.toggle("text-text-muted", names.length === 0)
      this.displayTarget.classList.toggle("text-gray-900", names.length > 0)
    }
    this._renderChips(ids)
  }

  // Chips under the field (Phelipe 2026-07-23) — same look as the
  // Specification/Drawings chips, with an x to unlink an activity.
  _renderChips(ids = null) {
    if (!this.hasChipsTarget) return
    const list = ids || this._multiIds()
    if (list.length === 0) {
      this.chipsTarget.innerHTML = ""
      this.chipsTarget.classList.add("hidden")
      return
    }
    this.chipsTarget.classList.remove("hidden")
    this.chipsTarget.innerHTML = list.map(id => {
      const name = (this.optionsValue || []).find(o => String(o.id) === id)?.name || `#${id}`
      const shown = name.length > 40 ? name.substring(0, 37) + "..." : name
      return `<span class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-brand/10 text-brand text-[13px] max-w-full">
        <span class="truncate" title="${this._esc(name)}">${this._esc(shown)}</span>
        <button type="button" data-action="click->searchable-select#removeChip" data-id="${this._esc(id)}"
                class="shrink-0 p-0.5 rounded-full hover:bg-brand/20 cursor-pointer">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </span>`
    }).join("")
  }

  removeChip(event) {
    const id = event.currentTarget.dataset.id
    this._commitMulti(this._multiIds().filter(v => v !== id))
    if (this.hasBrowserTarget && !this.browserTarget.classList.contains("hidden")) {
      this._renderBrowser(this._browserQuery())
      this._updateBrowserCount()
    }
  }

  _browserQuery() {
    return this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : ""
  }

  _updateBrowserCount() {
    if (!this.hasBrowserCountTarget) return
    const n = this._multiIds().length
    this.browserCountTarget.textContent = n === 0 ? "None selected" : `${n} selected`
  }

  // ── AI suggestions filter (Phelipe 2026-07-23) ─────────────────
  // One click: ConsLog AI reads the in-progress action item and the
  // timeline narrows to just the activities it thinks apply (reasons show
  // as row tooltips). Click again to see the whole schedule.

  _aiContext() {
    const root = this.element.closest('[data-controller~="action-item-form"]') || document
    const title = root.querySelector('[data-action-item-form-target="titleInput"]')?.value?.trim() || ""
    const description = root.querySelector('[data-action-item-form-target="descriptionInput"]')?.value?.trim() || ""
    const kindSelect = root.querySelector('[data-action-item-form-target="kindSelect"]')
    const kind = kindSelect ? (kindSelect.selectedOptions[0]?.textContent || "").trim() : ""
    const filenames = Array.from(root.querySelectorAll(
      '[data-action-item-form-target="fileList"] span[title], [data-action-item-form-target="photoList"] span[title]'
    )).map(el => el.getAttribute("title")).filter(Boolean).slice(0, 10)
    return { title, description, kind, filenames }
  }

  async toggleAiSuggestions() {
    if (!this.suggestUrlValue) return

    if (this._aiFilterOn) {
      this._aiFilterOn = false
      this._paintAiBtn(false)
      this._setAiStatus("")
      this._renderBrowser(this._browserQuery())
      return
    }

    const ctx = this._aiContext()
    if (!ctx.title && !ctx.description) {
      this._setAiStatus("Give the action item a title or a description first — AI reads them to pick activities.")
      return
    }

    const digest = JSON.stringify([ctx.title, ctx.kind, ctx.description, ctx.filenames])
    if (this._aiSuggest && this._aiSuggest.digest === digest) {
      this._applyAiFilter()
      return
    }

    this._paintAiBtn(true)
    this._setAiStatus("", `<span class="font-bold ai-rainbow-text">ConsLog AI — reading the action item\u2026</span>`)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.suggestUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify(ctx)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "AI couldn't run")
      const reasons = new Map((data.suggestions || []).map(s => [String(s.id), s.reason || ""]))
      this._aiSuggest = { digest, reasons }
      this._applyAiFilter()
    } catch (err) {
      this._paintAiBtn(false)
      this._setAiStatus(err.message || "ConsLog AI couldn't run — try again.")
    }
  }

  _applyAiFilter() {
    const n = this._aiSuggest?.reasons?.size || 0
    if (n === 0) {
      this._aiFilterOn = false
      this._paintAiBtn(false)
      this._setAiStatus("No confident matches — add more detail to the item and try again.")
      this._renderBrowser(this._browserQuery())
      return
    }
    this._aiFilterOn = true
    this._paintAiBtn(true)
    this._setAiStatus(`Showing the ${n} activit${n === 1 ? "y" : "ies"} ConsLog AI picked — click the button again for the full schedule.`)
    this._renderBrowser(this._browserQuery())
  }

  _paintAiBtn(on) {
    if (!this.hasAiBtnTarget) return
    this.aiBtnTarget.classList.toggle("bg-brand", on)
    this.aiBtnTarget.classList.toggle("text-white", on)
    this.aiBtnTarget.classList.toggle("bg-gray-100", !on)
    this.aiBtnTarget.classList.toggle("text-gray-600", !on)
    this.aiBtnTarget.classList.toggle("hover:bg-gray-200", !on)
  }

  _setAiStatus(text, html = null) {
    if (!this.hasAiStatusTarget) return
    if (html) this.aiStatusTarget.innerHTML = html
    else this.aiStatusTarget.textContent = text || ""
    this.aiStatusTarget.classList.toggle("hidden", !(text || html))
  }

  closeBrowser() {
    if (this.hasBrowserTarget) this.browserTarget.classList.add("hidden")
  }

  browserBackdrop(event) {
    if (event.target === this.browserTarget) this.closeBrowser()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  browserFilter() {
    this._renderBrowser(this.browserSearchTarget.value.trim().toLowerCase())
  }

  // Huge Gantt view (Larry item 18): each activity renders as a bar over a
  // shared month axis (progress filled in), grouped by phase, with a today
  // line — so picking an activity means LOOKING at the schedule, not
  // scanning names. Rows without dates still list (no bar).
  _renderBrowser(query) {
    if (!this.hasBrowserListTarget) return

    const options = this.optionsValue || []
    const currentId = this.hasHiddenTarget ? this.hiddenTarget.value : ""
    const selectedSet = this.multiValue ? new Set(this._multiIds()) : new Set([String(currentId)].filter(Boolean))
    let filtered = query
      ? options.filter(o => `${o.name} ${o.group || ""}`.toLowerCase().includes(query))
      : options
    // AI filter on: only the suggested activities stay visible.
    const aiReasons = this._aiFilterOn ? this._aiSuggest?.reasons : null
    if (aiReasons) filtered = filtered.filter(o => aiReasons.has(String(o.id)))

    if (filtered.length === 0) {
      this.browserListTarget.innerHTML = `<div class="px-3 py-10 text-sm text-gray-400 text-center">${query ? "No matches" : "No schedule activities"}</div>`
      return
    }

    // Time domain across dated rows, padded a week each side.
    let min = null, max = null
    filtered.forEach(o => {
      if (!o.start || !o.end) return
      const s = new Date(o.start), e = new Date(o.end)
      if (!isNaN(s) && (!min || s < min)) min = s
      if (!isNaN(e) && (!max || e > max)) max = e
    })
    const hasTimeline = !!(min && max && max > min)
    if (hasTimeline) {
      min = new Date(min.getTime() - 7 * 86400000)
      max = new Date(max.getTime() + 7 * 86400000)
    }
    const span = hasTimeline ? (max - min) : 1
    const pos = (d) => Math.min(100, Math.max(0, ((d - min) / span) * 100))
    const today = new Date()
    const todayPct = hasTimeline && today >= min && today <= max ? pos(today) : null
    const todayLine = todayPct === null ? "" :
      `<div class="absolute top-0 bottom-0 w-px bg-red-400/60 pointer-events-none" style="left:${todayPct}%"></div>`

    const NAME_W = "w-80"

    // Month ticks header (sticky inside the scroll pane).
    let html = ""
    if (hasTimeline) {
      const months = []
      const cursor = new Date(min.getFullYear(), min.getMonth(), 1)
      while (cursor <= max) { months.push(new Date(cursor)); cursor.setMonth(cursor.getMonth() + 1) }
      const step = months.length > 30 ? Math.ceil(months.length / 30) : 1
      const ticks = months.filter((_, i) => i % step === 0).map(m => {
        const label = m.toLocaleDateString("en-US", { month: "short" }) +
          (m.getMonth() === 0 ? ` '${String(m.getFullYear()).slice(2)}` : "")
        return `<span class="absolute top-1/2 -translate-y-1/2 text-[11px] text-gray-400 whitespace-nowrap" style="left:${pos(m)}%">${label}</span>`
      }).join("")
      html += `<div class="sticky top-0 z-10 bg-white border-b border-gray-100 flex">
        <div class="${NAME_W} shrink-0 px-3 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Activity</div>
        <div class="flex-1 relative h-8 min-w-0">${ticks}${todayLine}</div>
      </div>`
    }

    // Group by phase (option.group), preserving first-seen order.
    const groups = []
    const byGroup = new Map()
    filtered.forEach(o => {
      const g = o.group || "Schedule"
      if (!byGroup.has(g)) { byGroup.set(g, []); groups.push(g) }
      byGroup.get(g).push(o)
    })

    const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })

    groups.forEach(g => {
      html += `<div class="px-3 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">${this._esc(g)}</div>`
      html += byGroup.get(g).map(o => {
        const selected = selectedSet.has(String(o.id))
        let bar = ""
        if (hasTimeline && o.start && o.end) {
          const left = pos(new Date(o.start))
          const width = Math.max(pos(new Date(o.end)) - left, 0.4)
          const pct = Math.min(Math.max(o.pct || 0, 0), 100)
          bar = `<div class="absolute top-1/2 -translate-y-1/2 h-3.5 rounded-full bg-brand/25 overflow-hidden" style="left:${left}%;width:${width}%;min-width:6px" title="${fmt(o.start)} – ${fmt(o.end)}${pct ? ` · ${pct}%` : ""}">
            <div class="h-full bg-brand" style="width:${pct}%"></div>
          </div>`
        }
        return `<div data-action="click->searchable-select#browserPick"
                     data-id="${o.id}" data-name="${this._esc(o.name)}"
                     class="flex items-center rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${selected ? 'bg-brand/5' : ''}">
          <div class="${NAME_W} shrink-0 px-3 py-1.5 flex items-center gap-2.5 min-w-0">
            ${this.multiValue
              ? (selected
                ? `<svg class="w-4 h-4 text-brand shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
                : `<div class="w-4 h-4 shrink-0 rounded border border-gray-300"></div>`)
              : `<div class="w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center ${selected ? 'border-brand' : 'border-gray-300'}">${selected ? '<div class="w-2 h-2 rounded-full bg-brand"></div>' : ''}</div>`}
            <span class="text-[13px] truncate ${selected ? 'text-brand font-medium' : 'text-gray-700'}" title="${this._esc(aiReasons?.get(String(o.id)) || o.name)}">${this._esc(o.name)}</span>
          </div>
          <div class="flex-1 relative h-7 min-w-0 border-l border-gray-50">${hasTimeline ? todayLine : ""}${bar}</div>
        </div>`
      }).join("")
    })
    this.browserListTarget.innerHTML = html
  }

  browserPick(event) {
    const { id, name } = event.currentTarget.dataset
    if (this.multiValue) {
      const ids = this._multiIds()
      const idx = ids.indexOf(String(id))
      if (idx >= 0) ids.splice(idx, 1)
      else ids.push(String(id))
      this._commitMulti(ids)
      this._renderBrowser(this._browserQuery())
      this._updateBrowserCount()
      return
    }
    this._commit(id, name)
    this.closeBrowser()
  }

  clear() {
    if (this.hasHiddenTarget) this.hiddenTarget.value = ""
    if (this.hasDisplayTarget) {
      this.displayTarget.value = ""
      this.displayTarget.placeholder = this.placeholderValue
      this.displayTarget.classList.remove("text-gray-900")
      this.displayTarget.classList.add("text-text-muted")
    }
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
